import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EQueryKeys } from 'constants/query-keys';
import { useMutation, useQuery } from 'react-query';
import { countryStateCityApi, subLimitsApi } from 'services/apis';
import { SubLimit, SubLimitsTableProps } from './types';
import { convertSubLimitsToPayload, validateSubLimit } from './forms';
import {
  DeleteSubLimitBySubmissionIdRequest,
  UpsertSubLimitsRequest,
} from 'libs/pinnacle-sdk';
import { handleError } from 'utils/form';
import ExcelTable from 'components/ui/excel-table';
import {
  ECellValueType,
  EExcelTableColumnType,
  EExcelTablePaginationType,
  ExcelTableColDef,
  RowData,
  EValidationSeverity,
} from 'components/ui/excel-table/types';
import { ECurrencies } from 'constants/enums';
import { isSubmissionEditable } from 'utils/submission';

const SubLimitsTable: React.FC<SubLimitsTableProps> = ({
  submission,
  onErrorChange,
}: SubLimitsTableProps) => {
  const { t } = useTranslation();

  const isEditingMode = useMemo(() => {
    return isSubmissionEditable(submission.status);
  }, [submission.status]);

  const { data: countries, isLoading: isLoadingCountries } = useQuery(
    EQueryKeys.COUNTRIES,
    async () => {
      return countryStateCityApi.getCountries();
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  const {
    data: subLimits,
    isLoading: isLoadingSubLimits,
    refetch: refetchSubLimits,
  } = useQuery(
    [EQueryKeys.SUB_LIMITS, submission.submissionId],
    () =>
      subLimitsApi.getSubLimits({
        submissionId: submission.submissionId,
      }),
    {
      enabled: !!submission.submissionId,
    }
  );

  const rowData: SubLimit[] = useMemo(
    () =>
      subLimits?.map((item, index) => {
        return {
          layerNumber: index + 1,
          country: item.country,
          currency: submission.originalCurrency || ECurrencies.USD,
          peril: item.peril,
          subLimitAmount: Number(item.amount),
          subLimitId: item.subLimitId,
        };
      }) || [],
    [subLimits, submission.originalCurrency]
  );

  const updateSubLimitSettingsMutation = useMutation(
    async (request: UpsertSubLimitsRequest) => {
      await subLimitsApi.upsertSubLimits(request);
    },
    {
      onError: async (error: { response: Response }) => {
        handleError({
          error,
          defaultMessage: t('subLimits.createdRecordsFailed'),
        });
      },
      onSuccess: () => {
        refetchSubLimits();
      },
    }
  );

  const upsertSubLimitData = useCallback(
    async (subLimits: SubLimit[]) => {
      const currentSubLimitsData = convertSubLimitsToPayload(subLimits);

      await updateSubLimitSettingsMutation.mutateAsync({
        submissionId: submission.submissionId,
        upsertSubLimitPayloadDto: currentSubLimitsData,
      });
    },
    [updateSubLimitSettingsMutation, submission.submissionId]
  );

  const deleteSubLimitMutation = useMutation(
    async (request: DeleteSubLimitBySubmissionIdRequest) => {
      await subLimitsApi.deleteSubLimitBySubmissionId(request);
    },
    {
      onError: async (error: { response: Response }) => {
        handleError({
          error,
          defaultMessage: t('subLimits.deleteRecordFailed'),
        });
      },
    }
  );

  const onDataChange = useCallback(
    async (affectedRow: RowData<SubLimit>) => {
      if (affectedRow.isError.strict) return;
      await upsertSubLimitData([affectedRow.data]);
    },
    [upsertSubLimitData]
  );

  const onDeleteRow = useCallback(
    async (subLimit: RowData<SubLimit>) => {
      if (subLimit.data.subLimitId) {
        await deleteSubLimitMutation.mutateAsync({
          subLimitId: subLimit.data.subLimitId,
          submissionId: submission.submissionId,
        });
      }
    },
    [deleteSubLimitMutation, submission.submissionId]
  );

  const canAddNewRow = useCallback(
    (tableData: SubLimit[]) => {
      const hasStrictErrors = tableData.some((row, index) => {
        const errors = validateSubLimit(row, index, tableData, t);
        return errors.some(
          (error) =>
            !error.severity || error.severity === EValidationSeverity.STRICT
        );
      });

      if (hasStrictErrors && tableData.length > 0) {
        return {
          canAdd: false,
          message: t('subLimits.fixErrorsBeforeAddingRow'),
        };
      }

      return {
        canAdd: true,
        message: undefined,
      };
    },
    [t]
  );

  const columnDefs = useMemo<ExcelTableColDef[]>(
    () => [
      {
        field: 'currency',
        headerName: t('subLimits.headers.currency'),
        flex: 1,
        minWidth: 108,
        editable: false,
      },
      {
        field: 'country',
        headerName: t('subLimits.headers.country'),
        flex: 2,
        minWidth: 190,
        editable: isEditingMode,
        columnType: EExcelTableColumnType.SELECTION,
        selectionOptions:
          countries?.map((country) => ({
            value: country.name,
            label: country.name,
          })) || [],
      },
      {
        field: 'peril',
        headerName: t('subLimits.headers.peril'),
        flex: 2,
        minWidth: 250,
        width: 250,
        editable: isEditingMode,
        columnType: EExcelTableColumnType.SELECTION,
        selectionOptions: [
          {
            value: t('subLimits.perils.wind'),
            label: t('subLimits.perils.wind'),
          },
          {
            value: t('subLimits.perils.flood'),
            label: t('subLimits.perils.flood'),
          },
          {
            value: t('subLimits.perils.earthquake'),
            label: t('subLimits.perils.earthquake'),
          },
        ],
      },
      {
        field: 'subLimitAmount',
        headerName: t('subLimits.headers.subLimitAmount'),
        flex: 1,
        minWidth: 150,
        editable: isEditingMode,
        type: ECellValueType.NUMERIC_COLUMN,
      },
    ],
    [t, countries, isEditingMode]
  );

  return (
    <div className="ag-theme-alpine">
      <ExcelTable
        rowKeyColName="layerNumber"
        tableContainerClassName="h-[15rem]"
        loading={isLoadingCountries || isLoadingSubLimits}
        columnDefs={columnDefs}
        data={rowData}
        tableClassName="rounded-t-lg"
        onErrorChange={({ strict }) => {
          onErrorChange?.(strict);
        }}
        className="mb-4"
        paginationType={EExcelTablePaginationType.INFO}
        includeIdRow={true}
        includeAddRow={isEditingMode}
        includeDeleteCol={isEditingMode}
        newRowData={{
          currency: submission.originalCurrency || ECurrencies.USD,
          country: '',
          peril: '',
          subLimitAmount: 0,
        }}
        validateRow={validateSubLimit}
        canAddRow={canAddNewRow}
        onDeleteRow={onDeleteRow}
        onDataChange={onDataChange}
      />
    </div>
  );
};

export default SubLimitsTable;
